import React, { useEffect, useMemo, useState } from "react";
import api, { dkk, dateDK, formatErr } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  ChevronRight, FileSpreadsheet, Pencil, Trash2, Search,
  CalendarDays, Hash, X,
} from "lucide-react";
import { toast } from "sonner";

export default function CsvHistory() {
  const [imports, setImports] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // { import_id: boolean }
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [deleting, setDeleting] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/csv-imports"),
      api.get("/transactions", { params: { limit: 5000 } }),
    ])
      .then(([impRes, txRes]) => {
        setImports(impRes.data || []);
        setTransactions(txRes.data || []);
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const txById = useMemo(() => {
    const m = {};
    transactions.forEach((t) => { if (t.id) m[t.id] = t; });
    return m;
  }, [transactions]);

  // Transaktioner der ikke hører til en kendt import (legacy/manuelt indtastede)
  const orphanTransactions = useMemo(() => {
    const known = new Set();
    imports.forEach((imp) => (imp.transaction_ids || []).forEach((id) => known.add(id)));
    return transactions.filter((t) => !t.import_id && !known.has(t.id));
  }, [imports, transactions]);

  const filteredTx = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return transactions;
    return transactions.filter((t) => {
      const hay = [
        t.license_plate, t.description, t.transaction_type, t.date,
        t.booking_id, String(t.total_earnings || ""),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [transactions, search]);

  const filteredOrphans = useMemo(() => {
    if (!search) return orphanTransactions;
    const orphanIds = new Set(orphanTransactions.map((t) => t.id));
    return filteredTx.filter((t) => orphanIds.has(t.id));
  }, [orphanTransactions, filteredTx, search]);

  const deleteTx = async (tx) => {
    if (!window.confirm(`Slet transaktion på ${tx.license_plate} (${tx.date}, ${dkk(tx.total_earnings || 0)})?`)) return;
    setDeleting((s) => ({ ...s, [tx.id]: true }));
    try {
      await api.delete(`/transactions/${tx.id}`);
      setTransactions((arr) => arr.filter((x) => x.id !== tx.id));
      toast.success("Transaktion slettet");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setDeleting((s) => { const c = { ...s }; delete c[tx.id]; return c; });
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]" data-testid="csv-history-page">
      <div className="mb-6 sm:mb-10">
        <div className="overline text-zinc-400">Revisor</div>
        <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2">CSV historik.</h1>
        <p className="text-zinc-500 text-xs sm:text-sm mt-1">
          Alle CSV-uploads og deres transaktioner — søg, redigér eller slet enkelt-poster.
        </p>
      </div>

      <div className="relative mb-6 max-w-xl">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          placeholder="Søg nummerplade, beskrivelse, type, dato eller booking-ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-none border-zinc-700"
          data-testid="csv-history-search"
        />
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Indlæser…</div>
      ) : (
        <>
          {/* Imports */}
          <div className="space-y-2 mb-10">
            <div className="overline text-zinc-500 mb-3">Uploads ({imports.length})</div>
            {imports.length === 0 && (
              <div className="section-card p-6 text-center text-zinc-500 text-sm">
                Ingen CSV-uploads endnu. Gå til Omsætning og importér en CSV.
              </div>
            )}
            {imports.map((imp) => {
              const isOpen = !!expanded[imp.id];
              const impTxs = (imp.transaction_ids || []).map((id) => txById[id]).filter(Boolean);
              const visible = search ? impTxs.filter((t) => filteredTx.some((f) => f.id === t.id)) : impTxs;
              return (
                <div key={imp.id} className="section-card" data-testid={`import-${imp.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded((s) => ({ ...s, [imp.id]: !isOpen }))}
                    className="w-full p-3 sm:p-4 flex items-center gap-3 hover:bg-[#262626] transition-colors text-left"
                    data-testid={`import-toggle-${imp.id}`}
                  >
                    <ChevronRight className={"w-4 h-4 text-zinc-500 transition-transform " + (isOpen ? "rotate-90" : "")} />
                    <FileSpreadsheet className="w-4 h-4 text-[#C8A661]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold tracking-tight text-white truncate">{imp.filename || "(uden navn)"}</div>
                      <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {dateDK(imp.uploaded_at)}</span>
                        <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" /> {imp.rows_total} linjer</span>
                        <span className="text-emerald-400">{imp.inserted_count} ny</span>
                        <span className="text-zinc-500">{imp.updated_count} opdateret</span>
                        <span className="text-zinc-500">{(imp.plates_seen || []).length} biler</span>
                        {(imp.new_plates || []).length > 0 && (
                          <span className="text-[#C8A661]">+ {imp.new_plates.length} ny bil oprettet</span>
                        )}
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-zinc-800 overflow-x-auto">
                      <TxTable
                        list={visible}
                        onEdit={setEditTarget}
                        onDelete={deleteTx}
                        deleting={deleting}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Orphan / manually entered transactions */}
          {orphanTransactions.length > 0 && (
            <div className="mb-10">
              <div className="overline text-zinc-500 mb-3">
                Øvrige transaktioner ({orphanTransactions.length})
                <span className="ml-2 text-[10px] text-zinc-600 normal-case tracking-normal">— uden tilknyttet CSV-upload (typisk manuelt indtastede eller ældre data)</span>
              </div>
              <div className="section-card overflow-x-auto">
                <TxTable
                  list={filteredOrphans}
                  onEdit={setEditTarget}
                  onDelete={deleteTx}
                  deleting={deleting}
                />
              </div>
            </div>
          )}
        </>
      )}

      <TxEditDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={(updated) => {
          setTransactions((arr) => arr.map((t) => t.id === updated.id ? updated : t));
          setEditTarget(null);
        }}
      />
    </div>
  );
}

function TxTable({ list, onEdit, onDelete, deleting }) {
  if (!list || list.length === 0) {
    return <div className="p-6 text-center text-zinc-500 text-xs">Ingen transaktioner.</div>;
  }
  return (
    <table className="w-full text-xs min-w-[900px]">
      <thead className="bg-[#1F1F1F]">
        <tr className="text-zinc-500 uppercase tracking-wider text-[10px]">
          <th className="text-left py-2 px-3">Dato</th>
          <th className="text-left py-2 px-3">Plade</th>
          <th className="text-left py-2 px-3">Type</th>
          <th className="text-left py-2 px-3">Beskrivelse</th>
          <th className="text-right py-2 px-3">Indtjening</th>
          <th className="text-right py-2 px-3">Km</th>
          <th className="text-left py-2 px-3">Booking</th>
          <th className="text-right py-2 px-3 w-[80px]"></th>
        </tr>
      </thead>
      <tbody>
        {list.map((t) => (
          <tr key={t.id} className="border-b border-zinc-900 hover:bg-[#262626] transition-colors" data-testid={`tx-row-${t.id}`}>
            <td className="py-2 px-3 text-zinc-300 tabular-nums">{t.date ? dateDK(t.date) : "—"}</td>
            <td className="py-2 px-3 font-display font-bold text-white">{t.license_plate}</td>
            <td className="py-2 px-3 text-zinc-400">{t.transaction_type || "—"}</td>
            <td className="py-2 px-3 text-zinc-400 max-w-[260px] truncate" title={t.description}>{t.description || "—"}</td>
            <td className={"py-2 px-3 text-right tabular-nums " + ((t.total_earnings || 0) > 0 ? "text-emerald-400" : (t.total_earnings || 0) < 0 ? "text-red-400" : "text-zinc-600")}>
              {t.total_earnings ? dkk(t.total_earnings) : "—"}
            </td>
            <td className="py-2 px-3 text-right tabular-nums text-zinc-500">{t.km_driven || "—"}</td>
            <td className="py-2 px-3 text-zinc-500 text-[10px]">{t.booking_id || "—"}</td>
            <td className="py-2 px-3 text-right whitespace-nowrap">
              <button
                onClick={() => onEdit(t)}
                className="text-zinc-500 hover:text-[#C8A661] p-1.5"
                title="Redigér"
                data-testid={`tx-edit-${t.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onDelete(t)}
                disabled={!!deleting[t.id]}
                className="text-zinc-500 hover:text-red-400 p-1.5 disabled:opacity-30"
                title="Slet"
                data-testid={`tx-delete-${t.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TxEditDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setForm({
      date: target.date || "",
      license_plate: target.license_plate || "",
      transaction_type: target.transaction_type || "",
      description: target.description || "",
      total_earnings: target.total_earnings ?? "",
      km_driven: target.km_driven ?? "",
      booking_id: target.booking_id || "",
    });
  }, [target]);

  if (!target) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      ["total_earnings", "km_driven"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
        else if (payload[k] !== null) payload[k] = Number(payload[k]);
      });
      const res = await api.patch(`/transactions/${target.id}`, payload);
      toast.success("Transaktion gemt");
      onSaved(res.data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#1A1A1A] border-zinc-700 max-w-lg rounded-none" data-testid="tx-edit-dialog">
        <DialogHeader>
          <div className="overline text-[#C8A661] mb-1">Redigér transaktion</div>
          <DialogTitle className="headline text-2xl">
            {target.license_plate}{" "}
            <span className="text-zinc-400 font-normal text-base">· {target.date}</span>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <FieldRow>
            <FieldInput label="Dato (YYYY-MM-DD)" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="tx-edit-date" />
            <FieldInput label="Nummerplade" value={form.license_plate} onChange={(v) => setForm({ ...form, license_plate: v })} testid="tx-edit-plate" />
          </FieldRow>
          <FieldRow>
            <FieldInput label="Type" value={form.transaction_type} onChange={(v) => setForm({ ...form, transaction_type: v })} testid="tx-edit-type" />
            <FieldInput label="Booking-ID" value={form.booking_id} onChange={(v) => setForm({ ...form, booking_id: v })} testid="tx-edit-booking" />
          </FieldRow>
          <FieldInput label="Beskrivelse" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="tx-edit-description" />
          <FieldRow>
            <FieldInput label="Indtjening (DKK)" type="number" value={form.total_earnings} onChange={(v) => setForm({ ...form, total_earnings: v })} testid="tx-edit-earnings" />
            <FieldInput label="Km kørt" type="number" value={form.km_driven} onChange={(v) => setForm({ ...form, km_driven: v })} testid="tx-edit-km" />
          </FieldRow>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-700">Annullér</Button>
            <Button type="submit" disabled={busy} className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574]" data-testid="tx-edit-save">
              {busy ? "Gemmer…" : "Gem"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function FieldInput({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="overline">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-none border-zinc-700 mt-1.5"
        data-testid={testid}
      />
    </div>
  );
}
